import React, { useState } from 'react';
import { submitRegistration } from '../lib/dataService';
import { toast } from 'sonner';
import { Send, Users, ClipboardList, ShieldCheck, UserCheck, UserPlus, X } from 'lucide-react';
import './registration.css';

export const LEAGUE_OPTIONS = [
  'Under 10 Boys', 'Under 10 Girls',
  'Under 12 Boys', 'Under 12 Girls',
  'Under 13 Boys', 'Under 13 Girls',
  'Under 14 Boys', 'Under 14 Girls',
  'Under 15 Boys', 'Under 15 Girls',
  'Under 16 Boys', 'Under 16 Girls',
  'Under 19 Boys', 'Under 19 Girls',
  "Senior Women's League",
  "National Men's League - Division 1",
  "National Men's League - Division 2",
  "National Men's League - Division 3",
  'Corporate Basketball League',
  "Over 40 Men's League",
  "Over 40 Women's League",
];

const EMPTY_PLAYER = { name: '', dob: '', jersey: '', position: '', nationality: '', height: '', weight: '', organization: '' };
const MAX_PLAYERS = 15;

export default function RegistrationForm({ defaultLeague = '', pageSlug = '' }) {
  const [form, setForm] = useState({
    team_name: '', league: defaultLeague, primary_colour: '', secondary_colour: '',
    coach_name: '', coach_email: '', coach_phone: '',
    assistant_name: '', assistant_email: '', assistant_phone: '',
    manager_name: '', manager_email: '', manager_phone: '',
  });
  const [players, setPlayers] = useState(Array.from({ length: 10 }, () => ({ ...EMPTY_PLAYER })));
  const [declaration, setDeclaration] = useState(false);
  const [sending, setSending] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  // Corporate league: only Name + DOB are mandatory. Other leagues: Name, DOB, Position, Nationality.
  const isCorporate = (form.league || '').toLowerCase().includes('corporate');

  const set = (key) => (e) => setForm({ ...form, [key]: e.target.value });
  const setPlayer = (i, key) => (e) => {
    const next = players.slice();
    next[i] = { ...next[i], [key]: e.target.value };
    setPlayers(next);
  };

  const addPlayer = () => {
    if (players.length >= MAX_PLAYERS) {
      toast.error(`A team can have a maximum of ${MAX_PLAYERS} players.`);
      return;
    }
    setPlayers([...players, { ...EMPTY_PLAYER }]);
  };

  const removePlayer = (i) => {
    if (players.length <= 10) return;
    setPlayers(players.filter((_, idx) => idx !== i));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const named = players
      .map((p, idx) => ({ ...p, _row: idx + 1 }))
      .filter((p) => p.name.trim());
    if (!form.team_name.trim()) { toast.error('Please enter your team name.'); return; }
    if (!form.league) { toast.error('Please select a league.'); return; }
    if (!form.coach_name.trim() || !form.coach_email.trim()) { toast.error('Coach name and email are required.'); return; }
    if (!form.manager_name.trim() || !form.manager_email.trim()) { toast.error('Team manager name and email are required.'); return; }
    if (named.length < 5) { toast.error('Please enter at least 5 players on your roster.'); return; }
    for (const p of named) {
      if (!p.dob.trim()) { toast.error(`Player ${p._row} (${p.name}): date of birth is required.`); return; }
      if (isCorporate) {
        if (!p.organization.trim()) { toast.error(`Player ${p._row} (${p.name}): organization is required.`); return; }
      } else {
        if (!p.position.trim()) { toast.error(`Player ${p._row} (${p.name}): position is required.`); return; }
        if (!p.nationality.trim()) { toast.error(`Player ${p._row} (${p.name}): nationality is required.`); return; }
      }
    }
    if (!declaration) { toast.error('Please accept the Declaration & Media Consent.'); return; }
    setSending(true);
    try {
      const cleanPlayers = players.map(({ _row, ...p }) => p);
      await submitRegistration({ ...form, players: cleanPlayers, declaration, page_slug: pageSlug });
      setSubmitted(true);
      toast.success('Registration submitted! A copy has been emailed to CIBA and your team officials.');
    } catch (err) {
      const msg = err?.response?.data?.detail || 'Failed to submit registration. Please try again.';
      toast.error(msg);
    }
    setSending(false);
  };

  if (submitted) {
    return (
      <div className="reg-form-wrap">
        <div className="reg-success">
          <ShieldCheck size={44} />
          <h3>Registration Received!</h3>
          <p>Thank you — <strong>{form.team_name}</strong> has been registered for the <strong>{form.league}</strong>.</p>
          <p>A confirmation has been emailed to CIBA, your coach and your team manager. We will be in touch soon.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="reg-form-wrap" id="register">
      <div className="reg-header">
        <h2>Basketball Team Registration</h2>
        <p>Complete the information below to register your team with the Cayman Islands Basketball Association. Fields with a light yellow background are fillable — fields marked <span className="reg-req">*</span> are required.</p>
      </div>

      <form onSubmit={handleSubmit}>
        {/* Team Information */}
        <section className="reg-card">
          <h3><ClipboardList size={19} /> Team Information</h3>
          <div className="reg-grid reg-grid-2">
            <label>Team Name <span className="reg-req">*</span>
              <input type="text" className="reg-input" value={form.team_name} onChange={set('team_name')} required />
            </label>
            <label>League <span className="reg-req">*</span>
              <select className="reg-input" value={form.league} onChange={set('league')} required>
                <option value="">Select a league</option>
                {LEAGUE_OPTIONS.map((l) => <option key={l} value={l}>{l}</option>)}
              </select>
            </label>
            <label>Primary Team Colour
              <input type="text" className="reg-input" value={form.primary_colour} onChange={set('primary_colour')} />
            </label>
            <label>Secondary Team Colour
              <input type="text" className="reg-input" value={form.secondary_colour} onChange={set('secondary_colour')} />
            </label>
          </div>
        </section>

        {/* Team Officials */}
        <section className="reg-card">
          <h3><UserCheck size={19} /> Team Officials</h3>
          <div className="reg-official">
            <h4>Head Coach</h4>
            <div className="reg-grid reg-grid-3">
              <label>Full Name <span className="reg-req">*</span>
                <input type="text" className="reg-input" value={form.coach_name} onChange={set('coach_name')} required />
              </label>
              <label>Email <span className="reg-req">*</span>
                <input type="email" className="reg-input" value={form.coach_email} onChange={set('coach_email')} required />
              </label>
              <label>Phone <span className="reg-req">*</span>
                <input type="tel" className="reg-input" value={form.coach_phone} onChange={set('coach_phone')} required />
              </label>
            </div>
          </div>
          <div className="reg-official">
            <h4>Assistant Coach <span className="reg-optional">(optional)</span></h4>
            <div className="reg-grid reg-grid-3">
              <label>Full Name
                <input type="text" className="reg-input" value={form.assistant_name} onChange={set('assistant_name')} />
              </label>
              <label>Email
                <input type="email" className="reg-input" value={form.assistant_email} onChange={set('assistant_email')} />
              </label>
              <label>Phone
                <input type="tel" className="reg-input" value={form.assistant_phone} onChange={set('assistant_phone')} />
              </label>
            </div>
          </div>
          <div className="reg-official">
            <h4>Team Manager</h4>
            <div className="reg-grid reg-grid-3">
              <label>Full Name <span className="reg-req">*</span>
                <input type="text" className="reg-input" value={form.manager_name} onChange={set('manager_name')} required />
              </label>
              <label>Email <span className="reg-req">*</span>
                <input type="email" className="reg-input" value={form.manager_email} onChange={set('manager_email')} required />
              </label>
              <label>Phone <span className="reg-req">*</span>
                <input type="tel" className="reg-input" value={form.manager_phone} onChange={set('manager_phone')} required />
              </label>
            </div>
          </div>
        </section>

        {/* Roster */}
        <section className="reg-card">
          <h3><Users size={19} /> Team Roster <span className="reg-optional">(minimum 5, maximum {MAX_PLAYERS} players)</span></h3>
          <p className="reg-roster-note">
            {isCorporate
              ? <>For the Corporate Basketball League, <strong>Name</strong>, <strong>Date of Birth</strong> and <strong>Organization</strong> are required for each player. Note: a maximum of 3 players from outside your organization are allowed on any team.</>
              : <><strong>Name</strong>, <strong>Date of Birth</strong>, <strong>Position</strong> and <strong>Nationality</strong> are required for each player.</>}
          </p>
          <div className="reg-roster-scroll">
            <table className="reg-roster">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Full Name <span className="reg-req">*</span></th>
                  <th>Date of Birth <span className="reg-req">*</span></th>
                  <th>Jersey #</th>
                  <th>Pos {!isCorporate && <span className="reg-req">*</span>}</th>
                  {isCorporate ? (
                    <th>Organization <span className="reg-req">*</span></th>
                  ) : (
                    <>
                      <th>Nationality <span className="reg-req">*</span></th>
                      <th>Height</th>
                      <th>Weight</th>
                    </>
                  )}
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {players.map((p, i) => (
                  <tr key={i}>
                    <td className="reg-roster-num">{i + 1}</td>
                    <td><input type="text" className="reg-input" value={p.name} onChange={setPlayer(i, 'name')} placeholder={i < 5 ? 'Required' : 'Optional'} /></td>
                    <td><input type="date" className="reg-input" value={p.dob} onChange={setPlayer(i, 'dob')} /></td>
                    <td><input type="text" className="reg-input reg-narrow" value={p.jersey} onChange={setPlayer(i, 'jersey')} /></td>
                    <td>
                      <select className="reg-input reg-narrow" value={p.position} onChange={setPlayer(i, 'position')}>
                        <option value=""></option>
                        <option value="G">G</option>
                        <option value="F">F</option>
                        <option value="C">C</option>
                      </select>
                    </td>
                    {isCorporate ? (
                      <td><input type="text" className="reg-input" value={p.organization} onChange={setPlayer(i, 'organization')} placeholder="Company / organization" /></td>
                    ) : (
                      <>
                        <td><input type="text" className="reg-input" value={p.nationality} onChange={setPlayer(i, 'nationality')} /></td>
                        <td><input type="text" className="reg-input reg-narrow" value={p.height} onChange={setPlayer(i, 'height')} /></td>
                        <td><input type="text" className="reg-input reg-narrow" value={p.weight} onChange={setPlayer(i, 'weight')} /></td>
                      </>
                    )}
                    <td>
                      {i >= 10 && (
                        <button type="button" className="reg-remove-player" onClick={() => removePlayer(i)} title="Remove player">
                          <X size={15} />
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {players.length < MAX_PLAYERS ? (
            <button type="button" className="reg-add-player" onClick={addPlayer}>
              <UserPlus size={16} /> Add Player ({players.length}/{MAX_PLAYERS})
            </button>
          ) : (
            <p className="reg-roster-note">Maximum of {MAX_PLAYERS} players reached.</p>
          )}
        </section>

        {/* Declaration */}
        <section className="reg-card">
          <h3><ShieldCheck size={19} /> Declaration & Media Consent</h3>
          <label className="reg-declaration">
            <input type="checkbox" checked={declaration} onChange={(e) => setDeclaration(e.target.checked)} />
            <span>
              I confirm that the information provided is accurate and that all listed players are eligible to participate.
              I consent to photographs and video taken during CIBA competitions being used for promotional purposes,
              and I agree to abide by the CIBA Code of Conduct and league rules and regulations. <span className="reg-req">*</span>
            </span>
          </label>
        </section>

        <button type="submit" className="btn-primary reg-submit" disabled={sending}>
          <Send size={16} /> {sending ? 'Submitting...' : 'Submit Registration'}
        </button>
      </form>
    </div>
  );
}
